import { Card, Checkbox, CircularProgress, FormControlLabel, Grid, Switch, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import TableOptionsBar, { RangeControlProps, SelectControl } from "./tableOptionsBar";
import TablePaginationBar from "./tablePaginationBar";

import './index.css'
import TableHead, { TableHeadRows } from "./TableHead";
import { formatDateToTimestamp } from "../../utils/date";
import ImageProfileCircle from "../ImageProfileCircle";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { SeeInformationAs, setSeeInformationAs } from "../../store/slices/auth";


export type TableRow<TKey extends string, TValue> = {
    [key in TKey]: TValue;
};

export type Sort = "ASC" | "DESC"

export interface SelectionOptions {
    selectedItems?: string[];
    selectorFieldName: string;
    setSelectedItems: (selectedItems: string[]) => void;
    setDeletedItem?: (deletedItem: string | string[]) => void;
    setSelectedItem?: (selectedItem: string | string[]) => void;
    timeToResetSelectedItems?: string;
}

export interface TableProps {
    tableHeadRows: TableHeadRows[];
    tableRows: TableRow<string, any>[];
    // refreshData?: () => void;
    // refreshAction?: () => void;
    loading: boolean;
    setOrderedData?: (sortField: string, sortType: Sort, orderedItems: TableRow<string, any>[]) => void,
    sortField?: string;
    sortType?: Sort;
    dateFilterField?: string;

    totalItems?: number;
    totalPages?: number;
    pageNumber?: number;
    itemsPerPage?: number;
    onChangePageNumber?: (value: number) => void;
    onChangeItemsPerPage?: (value: number) => void;
    onChangeSearchInputString?: (value: string) => void;
    searchInputString?: string;
    calculateLimitDate?: boolean
    hiddenSearchInput?: boolean

    rangeControlProps?: RangeControlProps;
    customStartItemOptionBar?: any,
    customItemOptionBarMiddle?: any,
    customItemOptionBar?: any,
    // downloadData?: () => void;
    // setSearchValue?: (value: string) => void
    selectControlProps?: SelectControl;
    firstSelectControlProps?: SelectControl;
    hiddenItemsPerPage?: boolean;
    hiddenBottomPaginator?: boolean;
    onlyTable?: boolean;
    selectionOptions?: SelectionOptions;
    cardComponent?: {
        imageData?: {
            descriptionRowName: string;
            size?: string;
        },
        numberOfCards?: number;
        changeViewOptionHidden?: boolean;
        cardContent: (row: TableRow<string, any>) => JSX.Element,
        onClick?: (row: TableRow<string, any>) => void,
        className?: (row: TableRow<string, any>) => string,
    },
    tableOptionsBar: {
        useDrawer: boolean;
    },
    hiddenFilterOptions?: boolean;
}


const MyTable = ({
    tableHeadRows, 
    tableRows, 
    // refreshAction, 
    loading, 
    setOrderedData, 
    sortField,
    sortType, 
    dateFilterField,

    totalItems,
    totalPages,
    pageNumber,
    itemsPerPage,
    onChangePageNumber,
    onChangeItemsPerPage,
    onChangeSearchInputString,
    searchInputString,
    calculateLimitDate,
    hiddenSearchInput,
    rangeControlProps,
    selectControlProps,
    firstSelectControlProps,
    hiddenItemsPerPage,
    hiddenBottomPaginator,
    customStartItemOptionBar,
    customItemOptionBarMiddle,
    customItemOptionBar,
    onlyTable,
    selectionOptions,
    cardComponent,
    tableOptionsBar,
    hiddenFilterOptions,
}: TableProps) => {
    const theme = useTheme();
    const {seeInformationAs} = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const [page, setPage] = useState(pageNumber || 1)
    const [itemsPerPagePaginator, setItemsPerPagePaginator] = useState(itemsPerPage || 10)
    const [startItemIndex, setStartItemIndex] = useState(0)
    const [finishtemIndex, setFinishtemIndex] = useState(9)
    const [searchStringValue, setSearchStringValue] = useState(searchInputString || "")
    const [finalItemRows, setFinalItemRows] = useState<TableRow<string, any>[]>([]);
    const [finalItemRowsObjects, setFinalItemRowsObjects] = useState<{ [key: string]: any }[]>([]);
    
    const [itemsSort, setItemsSort] = useState<Sort | undefined>(sortType)
    const [minDate, setMinDate] = useState<Date | null>(null)
    const [maxDate, setMaxDate] = useState<Date | null>(null)
    const [minRangeDateSelected, setMinRangeDateSelected] = useState<Date | null>(rangeControlProps?.startDate || null)
    const [maxRangeDateSelected, setMaxRangeDateSelected] = useState<Date | null>(rangeControlProps?.endDate ||null)
    const [itemsSortName, setItemsSortName] = useState<string | undefined>(sortField)
    const [optionControlFilter, setOptionControlFilter] = useState<{key?: string, value?: string}>({})
    const [selectedItems, setSelectedItems] = useState<string[]>(selectionOptions?.selectedItems || [])
    const [slicedItems, setSlicedItems] = useState<any[]>([])
    const [slicedItemsObjects, setSlicedItemsObjects] = useState<any[]>([])
    const [currentPageIds, setCurrentPageIds] = useState<string[]>([])
    const [currentSelectedAllItems, setCurrentSelectedAllItems] = useState<boolean>(false)
    const [drawerFiltersOpen, setDrawerFiltersOpen] = useState<boolean | undefined>(undefined);
    const [sortFields, setSortFields] = useState<{name: string; label: string}[]>([])
    


    useEffect(() => {
        setSelectedItems([])
        if (!selectionOptions?.setSelectedItems) {
            selectionOptions?.setSelectedItems([])
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionOptions?.timeToResetSelectedItems])

    const buildFinalItemRows = () => {
        if (loading || !tableRows || !tableRows.length === undefined) {
            return;
        }
        let tableRows2: any[] = [...tableRows];
        let finalTableRows: any[][] = [];
        let finalTableRowsObject: { [key: string]: any }[] = [];
        let rowsMinDate = null;
        let rowsMaxDate = null;
        for (let i = 0; i < tableRows2.length; i++) {
            const row = tableRows2[i];
            const itemColumns = []
            // const itemColumnsObject: { [key: string]: any } = {};
            let meetsSearchRequirement = (searchStringValue && searchStringValue.length < 3) || searchStringValue === "";
            if (optionControlFilter.key && optionControlFilter.value) {
                if (row[optionControlFilter.key] !== optionControlFilter.value) {
                    continue;
                }
            }
            for (const key in tableHeadRows) {
                const hRow = tableHeadRows[key];
                const tempLabel = hRow.label.toLowerCase();
                const value = ['number', 'string'].includes(typeof row[hRow.rowKeyName]) 
                                ? tempLabel.includes("fecha") || tempLabel.includes("fec.") 
                                    ? formatDateToTimestamp(row[hRow.rowKeyName])
                                    : row[hRow.rowKeyName] 
                                : "";
                if (totalPages !== undefined
                    || String(value).toLowerCase()
                        .includes(searchStringValue.toLocaleLowerCase()) 
                    || searchStringValue === ""
                ) {
                    meetsSearchRequirement = true;
                }
                itemColumns.push({
                    customValue: hRow?.customValue ? hRow.customValue(hRow, row) : null,
                    value,
                    style: hRow?.rowStyle ? hRow.rowStyle(row) : null,
                })
                // itemColumnsObject[hRow.rowKeyName] = value;
            }
            if (!dateFilterField) {
                if (meetsSearchRequirement) {
                    finalTableRows.push(itemColumns)
                    finalTableRowsObject.push(row)
                }
                continue;
            }
            const dateField = new Date(row[dateFilterField]);
            if (String(dateField) === "Invalid Date") {
                console.warn(`row ${i}: ${dateField}`)
            } else {
                dateField.setHours(12)
                if (!rowsMinDate || dateField < rowsMinDate) {
                    rowsMinDate = dateField;
                }
                if (!rowsMaxDate || dateField > rowsMaxDate) {
                    rowsMaxDate = dateField;
                }
            }
            // if (minRangeDateSelected) console.log(dateField.toISOString(), minRangeDateSelected?.toISOString(), maxRangeDateSelected?.toISOString())
            if (meetsSearchRequirement 
                && (!minRangeDateSelected || dateField >= minRangeDateSelected)
                && (!maxRangeDateSelected || dateField <= maxRangeDateSelected)
            ) {
                finalTableRows.push(itemColumns)
                finalTableRowsObject.push(row)
            }
            
        }
        if (!minRangeDateSelected && rowsMaxDate && calculateLimitDate !== false) {
            rowsMinDate?.setHours(0)
            setMinDate(rowsMinDate);
        }
        if (!maxRangeDateSelected && rowsMaxDate && calculateLimitDate !== false) {
            rowsMaxDate?.setHours(23)
            setMaxDate(rowsMaxDate);
        }
        setFinalItemRows(finalTableRows);
        setFinalItemRowsObjects(finalTableRowsObject);
    }

    useEffect(() => {
        buildFinalItemRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableRows, searchStringValue, maxRangeDateSelected, optionControlFilter]);

    useEffect(() => {
        if (!selectionOptions?.selectorFieldName) return;
        const headIndex = tableHeadRows.findIndex((hr: any) => hr.rowKeyName === selectionOptions.selectorFieldName);
        
        let sortFieldsData = tableHeadRows
                                .filter(hr => hr.allowOrderAction === true)
                                ?.map(hr => ({
                                    ...hr,
                                    name: hr.rowKeyName,
                                    label: hr.label,
                                }));
        if (sortFieldsData?.length) {
            setSortFields(sortFieldsData);
        }
        const currentIds = slicedItems.map(i => i[headIndex]?.value || i[headIndex]);
        setCurrentPageIds(currentIds);
        const currentSelectedItems = selectedItems.filter(i => currentIds.includes(i));
        setCurrentSelectedAllItems(currentSelectedItems.length === currentIds.length)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slicedItems]);

    useEffect(() => {
        let sortFieldsData = tableHeadRows
                                .filter(hr => hr.allowOrderAction === true)
                                ?.map(hr => ({
                                    ...hr,
                                    name: hr.rowKeyName,
                                    label: hr.label,
                                }));
        if (sortFieldsData?.length) {
            setSortFields(sortFieldsData);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableHeadRows]);

    useEffect(() => {
        if (!selectionOptions?.setSelectedItems) {
            return;
        }
        selectionOptions.setSelectedItems(selectedItems)
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItems]);

    useEffect(() => {
        setSlicedItems(totalPages !== undefined || onlyTable
            ? finalItemRows
            : finalItemRows.slice(startItemIndex, finishtemIndex + 1))
        setSlicedItemsObjects(totalPages !== undefined || onlyTable
            ? finalItemRowsObjects
            : finalItemRowsObjects.slice(startItemIndex, finishtemIndex + 1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finalItemRows, finishtemIndex]);

    const handleOrderAction = (th: TableHeadRows, i: number) => {
        if (!th.allowOrderAction) return;
        const rowName = th.rowKeyName;
        const sort = rowName !== itemsSortName || itemsSort === 'ASC'
                        ? 'DESC'
                        : 'ASC'
        console.log(th)
        const result = sortObjectsByKey([...tableRows], th.rowKeyName, sort, th.buildOrderValue);
        setItemsSort(sort);
        setItemsSortName(rowName);
        handlerChangePage(1);
        if (setOrderedData) {
            setOrderedData(rowName, sort, result)
        }
    };

    const sortObjectsByKey = (objects: any[], key: string, sort: Sort, buildRowData?: (value: string) => string) => {
        if (!objects || !objects.length || !key) {
          return objects;
        }
        const comparison = (a: any, b: any) => {
          const aValue = buildRowData ? buildRowData(a[key]) : a[key];
          const bValue = buildRowData ? buildRowData(b[key]) : b[key];
      
          if (aValue === undefined || bValue === undefined) {
            return 0;
          }
      
          if (sort === 'ASC') {
            return aValue < bValue ? -1 : (aValue > bValue ? 1 : 0);
          } else {
            return bValue < aValue ? -1 : (bValue > aValue ? 1 : 0);
          }
        };
      
        return objects.sort(comparison);
    }

    const handlerChangePage = (page: number) => {
        setPage(page)
        setStartItemIndex((page - 1) * itemsPerPagePaginator);
        setFinishtemIndex((page * itemsPerPagePaginator) -1)
        if (onChangePageNumber) {
            onChangePageNumber(page)
        }
    }

    const hadlerChangeItemsPerPage = (numberOfItems: number) => {
        handlerChangePage(1)
        setItemsPerPagePaginator(numberOfItems)
        if (onChangeItemsPerPage) {
            onChangeItemsPerPage(numberOfItems);
        }
    }

    const handlerChangeSelectControl = (key: string, value: string) => {
        setOptionControlFilter({key, value});
        setPage(1)
    }

    const getSelectionRowValue = (row: TableRow<string, any>) => {
        const fieldName = selectionOptions?.selectorFieldName;
        if (!fieldName) {
            return false;
        }
        if (cardComponent && (seeInformationAs === "card" || cardComponent.changeViewOptionHidden)) {
            return row[fieldName]
        }
        const headIndex = tableHeadRows.findIndex((hr: any) => hr.rowKeyName === fieldName);
        return row[headIndex]
    }

    const isSelected = (row: TableRow<string, any>): boolean => {
    
        const value = getSelectionRowValue(row)
        const finalValue = value?.value || value;
        return selectedItems.includes(finalValue);
    }

    const handlerSelectAnItem = (row: TableRow<string, any>) => {
        const value = getSelectionRowValue(row)
        const finalValue = value?.value || value;
        if (selectedItems.includes(finalValue)) {
            setSelectedItems(selectedItems.filter(r => r !== finalValue))
            if (selectionOptions?.setDeletedItem) {
                selectionOptions.setDeletedItem(finalValue)
            }
        } else {
            setSelectedItems([...selectedItems, finalValue]);
            if (selectionOptions?.setSelectedItem) {
                selectionOptions.setSelectedItem(finalValue)
            }
        }

    }

    const handlerSelectAllItems = () => {
        const cleanedItems = selectedItems.filter(i => !currentPageIds.includes(i));
        const selectionDifference = selectedItems.length - cleanedItems.length;
        if (selectionDifference < 0 || selectionDifference < currentPageIds.length) {
            setSelectedItems([...cleanedItems, ...currentPageIds]);
            setCurrentSelectedAllItems(true);
            if (selectionOptions?.setSelectedItem) {
                selectionOptions?.setSelectedItem(currentPageIds)
            }
        } else {
            setSelectedItems(cleanedItems);
            setCurrentSelectedAllItems(false);
            if (selectionOptions?.setDeletedItem) {
                selectionOptions?.setDeletedItem(currentPageIds)
            }
        }
    }

    const handleSeeInformationAs = (option: SeeInformationAs) => {
        dispatch(setSeeInformationAs(option));
    }

    const finalRangeControlProps = rangeControlProps || calculateLimitDate
        ? {
            ...(rangeControlProps || {}),
            maxDate,
            minDate,
            // startDate: rangeControlProps?.startDate || minRangeDateSelected, 
            // endDate: rangeControlProps?.endDate || maxRangeDateSelected, 
            setMinRangeDateSelected: (date: any) => {
                if (calculateLimitDate) {
                    setMinRangeDateSelected(date);
                } else if (rangeControlProps?.setStartDate && date) {
                    rangeControlProps.setStartDate(new Date(date))
                }
            },
            setMaxRangeDateSelected: (date: any) => {
                if (calculateLimitDate) {
                    setMaxRangeDateSelected(date);
                } else if (rangeControlProps?.setEndDate && date) {
                    rangeControlProps.setEndDate(new Date(date))
                }
                handlerChangePage(1);
            }

        }
        : undefined

    return (
        <Grid
            // style={{overflowX: 'auto',}}
        >
            {!onlyTable && <TableOptionsBar
                key={`${loading}`}
                loading={loading}
                rangeControlProps={finalRangeControlProps}
                // refreshAction={refreshAction}
                // resetTable={resetTable}
                hiddenFilterOptions={hiddenFilterOptions}
                searchValue={searchStringValue}
                setSearchValue={hiddenSearchInput
                    ? undefined
                    : (value: string) => {
                    // const finalString = value.length >= 3 ? value : "";
                    // if (finalString || finalString !== searchStringValue ) {
                        setSearchStringValue(value);
                        handlerChangePage(1)
                        if (onChangeSearchInputString) {
                            onChangeSearchInputString(value);
                        }
                    // }
                }}
                selectControlProps={
                    selectControlProps?.autocalculateFieldName
                        ?  {...selectControlProps, onChangeSelectControl: handlerChangeSelectControl}
                        : selectControlProps
                }
                firstSelectControlProps={
                    firstSelectControlProps?.autocalculateFieldName
                        ?  {...firstSelectControlProps, onChangeSelectControl: handlerChangeSelectControl}
                        : firstSelectControlProps
                }
                tableRows={tableRows}
                customStartItemOptionBar={customStartItemOptionBar}
                customItemOptionBarMiddle={customItemOptionBarMiddle}
                customItemOptionBar={customItemOptionBar}
                drawerFiltersOpen={drawerFiltersOpen}
                setDrawerFiltersOpen={setDrawerFiltersOpen}
                tableOptionsBar={tableOptionsBar}
            />}
            {!onlyTable && <TablePaginationBar 
                sortOptions={{
                    sortFieldsData: sortFields,
                    setSort: handleOrderAction,
                    sortField: itemsSortName,
                    sort: itemsSort,
                }}
                totalItems={totalItems || finalItemRows?.length || 0} 
                page={page} 
                setPage={handlerChangePage} 
                onChangePageQuantity={hadlerChangeItemsPerPage} 
                setStartItemIndex={setStartItemIndex}
                setFinishtemIndex={setFinishtemIndex}
                itemsPerPage={itemsPerPagePaginator}
                hiddenItemsPerPage={hiddenItemsPerPage}
                downloadData={{
                    tableHeadRows,
                    tableBodyRows: finalItemRows,
                }}
                drawerFiltersOpen={drawerFiltersOpen}
                setDrawerFiltersOpen={setDrawerFiltersOpen}
                oneRowPaginator={cardComponent?.numberOfCards === 1}
            />}
            {cardComponent && !cardComponent.changeViewOptionHidden &&
                <Grid container item width="100%" minHeight="60%">
                    <FormControlLabel 
                        control={
                            <Switch 
                                checked={seeInformationAs === "grid"}
                                onChange={(e, checked) => handleSeeInformationAs(checked ? "grid" : "card")}
                            />
                        } 
                        label="Ver grilla" 
                    />
                </Grid>
            }

            {cardComponent && (seeInformationAs === "card" || cardComponent.changeViewOptionHidden) && 
                <Grid container item width="100%" minHeight="60%" spacing={cardComponent.numberOfCards === 1 ? undefined :"1rem"} >
                        {!loading && slicedItemsObjects?.map((item, i) => {
                            return (
                            <Grid 
                                key={i} 
                                item 
                                xs={cardComponent.numberOfCards ? (12 / cardComponent.numberOfCards) : 12} 
                                sm={cardComponent.numberOfCards ? (12 / cardComponent.numberOfCards) : 6} 
                                md={cardComponent.numberOfCards ? (12 / cardComponent.numberOfCards) : 4} 
                                lg={cardComponent.numberOfCards ? (12 / cardComponent.numberOfCards) : 3} 
                                padding={cardComponent.numberOfCards === 1 ? ".5rem 0" : undefined}
                            >
                                <Card
                                    elevation={4}
                                    className={ 
                                        cardComponent?.className
                                            ? cardComponent?.className(item)
                                            : selectionOptions?.selectorFieldName
                                                ? isSelected(item)
                                                    ? "selectable-card-selected"
                                                    : "selectable-card"
                                                : undefined
                                    }
                                    
                                    onClick={ cardComponent && cardComponent?.onClick
                                        ? () => (cardComponent.onClick as any)(item)
                                        : () => handlerSelectAnItem(item)
                                    } 
                                    style={{
                                        minHeight: "4rem", 
                                        padding: ".5rem 0 0 0",
                                    }}
                                >
                                    {cardComponent.imageData && 
                                    <Grid container direction="row" alignItems="center" style={{ padding: ".5rem 1rem" }}>
                                        <Grid 
                                            item 
                                            style={{ 
                                                width: cardComponent.imageData.size 
                                                    ? cardComponent.imageData.descriptionRowName 
                                                    : "35px", 
                                                height: cardComponent.imageData.size 
                                                    ? cardComponent.imageData.descriptionRowName 
                                                    : "35px", 
                                                display: "flex", 
                                                justifyContent: "center"
                                            }}
                                        >
                                            <ImageProfileCircle
                                                userId={""}
                                                ruc={""}
                                                width={cardComponent.imageData.size 
                                                    ? cardComponent.imageData.descriptionRowName 
                                                    : "35px"
                                                }
                                                height={cardComponent.imageData.size 
                                                    ? cardComponent.imageData.descriptionRowName 
                                                    : "35px"
                                                }
                                                userName={item[cardComponent.imageData.descriptionRowName]}
                                            />
                                        </Grid>
                                      
                                        <Grid item style={{ flex: 1, paddingLeft: ".5rem" }}>
                                          <Typography
                                            gutterBottom
                                            variant="h6"
                                            component="div"
                                            sx={{
                                              minHeight: '2.8em',
                                              maxHeight: "3.8em",
                                              overflow: 'hidden',
                                              color: theme.s10palette.S10Blueblue700,
                                              alignContent: "center",
                                              textAlign: "start"
                                            }}
                                            fontSize=".8em"
                                          >
                                            {item[cardComponent.imageData.descriptionRowName]}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    }
                                    {cardComponent.cardContent(item)}
                                </Card>
                            </Grid>
                        )
                    })}
                </Grid>
            }
            {(!cardComponent || seeInformationAs === "grid") && !cardComponent?.changeViewOptionHidden && <div style={{overflowX: "auto", minHeight: "10rem"}} className="scroll-container">
                <table 
                    id="table-information"
                    style={{
                        width: "100%", 
                        borderSpacing: "0px 0rem",
                        borderRadius: "0.5rem",
                    }}
                >
                    <TableHead 
                        tableHeadRows={tableHeadRows} 
                        handleOrderAction={handleOrderAction}
                        allItemsSelected={selectedItems.length > 0 && currentSelectedAllItems}
                        setSelectionItems={selectionOptions?.selectorFieldName
                            ? () => handlerSelectAllItems()
                            : undefined
                        }
                    />
                    {!loading && <tbody>
                        {slicedItems.map((row, i) => (
                            <tr key={i} style={{height: "4rem"}}>
                                {selectionOptions?.selectorFieldName &&
                                    <td 
                                        style={{
                                            color: theme.s10palette.S10Greygrey600,
                                            width: "2rem",
                                        }}
                                    >
                                        <Checkbox 
                                            onClick={() => handlerSelectAnItem(row)} 
                                            checked={isSelected(row)}
                                        />
                                    </td>
                                }
                                {tableHeadRows.map((hKey: any, ii: number) => (
                                        <td key={`${i}-${ii}`} style={{
                                            color: theme.s10palette.S10Greygrey600,
                                            padding: "0.75rem 1rem",
                                            fontFamily: "Roboto",
                                            fontSize: "0.875rem",
                                            fontStyle: "normal",
                                            fontWeight: "400",
                                            lineHeight: "normal",
                                            borderBottom: "0.065rem solid #E8E8E8",
                                            textAlign: "start",
                                            ...(row[ii]?.style ? row[ii].style : {})
                                        }}>
                                            {row[ii]?.customValue || row[ii]?.value}
                                        </td>
                                    )
                                )}
                            </tr>))}
                    </tbody>}
                </table>
            </div>}
            {/* <Grid style={{height: `${40 - (finalItemRows.length * 4)}rem`}}> */}
            {onlyTable !== true && loading && <Grid 
                container
                style={{
                    height: "6rem",
                    // height: `${hiddenItemsPerPage ? 2 : 40 - (
                    //     loading 
                    //         ? 0 
                    //         : (finalItemRows.slice(startItemIndex, finishtemIndex + 1).length * 4))}rem`, 
                    width: "100%"
                }}
                direction="column"
                justifyContent="center"
                alignItems="center"
            >
                {loading && <>
                    <CircularProgress size="2rem" color="primary" />
                    <Typography
                        variant="body1"
                        marginTop="0.75rem"
                        style={{
                            color: theme.s10palette.S10Blueblue600,
                            textAlign: "center",
                            fontFamily: "Roboto",
                            fontSize: "1rem",
                            fontStyle: "normal",
                            fontWeight: "400",
                            lineHeight: "120%", /* 1.2rem */
                        }}
                    >
                        Actualizando...
                    </Typography>
                </>}
            </Grid>}
            {onlyTable !== true && !hiddenBottomPaginator && 
                <TablePaginationBar 
                    totalItems={totalItems || finalItemRows?.length || 0} 
                    page={page} 
                    setPage={handlerChangePage} 
                    onChangePageQuantity={hadlerChangeItemsPerPage} 
                    setStartItemIndex={setStartItemIndex}
                    setFinishtemIndex={setFinishtemIndex}
                    itemsPerPage={itemsPerPagePaginator}
                    hiddenItemsPerPage={hiddenItemsPerPage}
                    oneRowPaginator={cardComponent?.numberOfCards === 1}
                />}
        </Grid>
    )
}

export default MyTable;